import { ComponentProps, ReactElement, useCallback, useMemo } from 'react';
import TimePicker from 'antd/es/time-picker';
import clsx from 'clsx';
import dayjs, { Dayjs } from 'dayjs';

import { OpSkeleton } from 'new-components/DLS/OpSkeleton/OpSkeleton';

import { useOpFormContext } from '../../OpFormContext';
import { OpFormItem } from '../OpFormItem/OpFormItem';

import './OpFormTimeRangePicker.scss';

const { RangePicker } = TimePicker;

const DEFAULT_OFFSET_HOURS = 2;
const DEFAULT_OFFSET_MINUTES = 0;
const DISPLAY_FORMAT = 'h:mm a';

type RangeValue = [Dayjs | null, Dayjs | null] | null;

interface OpFormTimeRangePickerProps
  extends Pick<
    ComponentProps<typeof OpFormItem>,
    'name' | 'label' | 'tooltip' | 'rules'
  > {
  formItemClassName?: string;
  isLoading?: boolean;
  disabled?: boolean;
  testId?: string;
  gtm?: string;
  offsetHours?: number;
  offsetMinutes?: number;
  format?: string;
  minuteStep?: ComponentProps<typeof RangePicker>['minuteStep'];
  /** When true, stores only the formatted start time string (e.g. "11:00 am").
   *  When false (default), stores the raw Dayjs value from the picker. */
  returnOnlyTheHour?: boolean;
}

interface TimeRangeDisplayProps {
  value?: Dayjs;
  onChange?: (value: Dayjs | null) => void;
  offsetHours: number;
  offsetMinutes: number;
  format: string;
  minuteStep?: ComponentProps<typeof RangePicker>['minuteStep'];
  disabled?: boolean;
  testId?: string;
  gtm?: string;
}

const TimeRangeDisplay = ({
  value,
  onChange,
  offsetHours,
  offsetMinutes,
  format,
  minuteStep,
  disabled,
  testId,
  gtm,
}: TimeRangeDisplayProps): ReactElement => {
  const rangeValue = useMemo((): RangeValue => {
    if (!value?.isValid()) return null;
    const end = value.add(offsetHours, 'hour').add(offsetMinutes, 'minute');
    return [value, end];
  }, [value, offsetHours, offsetMinutes]);

  const handleCalendarChange = useCallback(
    (values: RangeValue) => {
      onChange?.(values?.[0] ?? null);
    },
    [onChange],
  );

  return (
    <RangePicker
      data-testid={testId}
      data-gtm={gtm}
      value={rangeValue}
      disabled={disabled ? [true, true] : [false, true]}
      format={format}
      minuteStep={minuteStep}
      needConfirm
      allowClear={false}
      onCalendarChange={handleCalendarChange}
    />
  );
};

export const OpFormTimeRangePicker = ({
  formItemClassName,
  name,
  label,
  tooltip,
  rules,
  isLoading,
  disabled,
  testId = 'op-form-time-range-picker',
  gtm,
  offsetHours = DEFAULT_OFFSET_HOURS,
  offsetMinutes = DEFAULT_OFFSET_MINUTES,
  format = DISPLAY_FORMAT,
  minuteStep,
  returnOnlyTheHour = false,
}: OpFormTimeRangePickerProps) => {
  const { isDataLoading, isReadOnly } = useOpFormContext();

  const getValueFromEvent = useCallback(
    (value: Dayjs | null) => (value?.isValid() ? value.format(format) : null),
    [format],
  );

  const getValueProps = useCallback(
    (value: unknown) => {
      if (typeof value !== 'string') return { value: undefined };
      const parsed = dayjs(value, format);
      return { value: parsed.isValid() ? parsed : undefined };
    },
    [format],
  );

  const formItemValueProps = returnOnlyTheHour
    ? { getValueFromEvent, getValueProps }
    : {};

  return (
    <OpFormItem
      className={clsx('op-form-time-range-picker', formItemClassName)}
      name={name}
      label={label}
      tooltip={tooltip}
      rules={rules}
      {...formItemValueProps}
    >
      {isDataLoading || isLoading ? (
        <OpSkeleton.Input active block />
      ) : (
        <TimeRangeDisplay
          offsetHours={offsetHours}
          offsetMinutes={offsetMinutes}
          format={format}
          minuteStep={minuteStep}
          disabled={isReadOnly || disabled}
          testId={testId}
          gtm={gtm}
        />
      )}
    </OpFormItem>
  );
};
